const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

export interface BrowserServiceEnvironment {
  isDevelopment: boolean;
  apiBaseUrl?: string;
  authBaseUrl?: string;
  djangoApiBaseUrl?: string;
}

export interface BrowserServiceEndpoints {
  apiBaseUrl: string;
  authBaseUrl: string;
  djangoApiBaseUrl: string;
}

function isLoopbackHost(hostname: string): boolean {
  return LOOPBACK_HOSTS.has(hostname);
}

export function resolveBrowserServiceBaseUrl(
  configuredBaseUrl: string | undefined,
  options: { defaultBaseUrl?: string } = {},
): string {
  const baseUrl = configuredBaseUrl || options.defaultBaseUrl || '';
  if (!baseUrl || typeof window === 'undefined') {
    return baseUrl;
  }

  try {
    const current = new URL(window.location.href);
    const target = new URL(baseUrl, current.origin);

    if (
      isLoopbackHost(current.hostname) &&
      isLoopbackHost(target.hostname) &&
      target.origin !== current.origin
    ) {
      return '';
    }
  } catch {
    return baseUrl;
  }

  return baseUrl;
}

/**
 * Resolve every browser-facing service endpoint from Vite's public build
 * environment. Empty production values deliberately select the current origin,
 * whose nginx boundary routes each API family to the owning service.
 */
export function resolveBrowserServiceEndpoints(
  environment: BrowserServiceEnvironment,
): BrowserServiceEndpoints {
  if (environment.isDevelopment) {
    return {
      apiBaseUrl: '',
      authBaseUrl: '',
      djangoApiBaseUrl: '',
    };
  }

  return {
    apiBaseUrl: resolveBrowserServiceBaseUrl(environment.apiBaseUrl, {
      defaultBaseUrl: '',
    }),
    authBaseUrl: resolveBrowserServiceBaseUrl(environment.authBaseUrl),
    djangoApiBaseUrl: resolveBrowserServiceBaseUrl(
      environment.djangoApiBaseUrl || environment.authBaseUrl,
    ),
  };
}
